import { useParams, useNavigate, Link } from "react-router-dom";
import { useContentRun, useContentPieces, useUpdatePiece } from "../hooks/useContent";
import { useApprovePiece, useRejectPiece, useRegeneratePiece } from "../hooks/useReview";
import { usePublishRun, usePublishStatus, useRetryPublish } from "../hooks/usePublishing";
import { ContentReview } from "../components/content/ContentReview";
import { PipelineStatus } from "../components/pipeline/PipelineStatus";
import { PublishButton } from "../components/publishing/PublishButton";
import { PublishStatus } from "../components/publishing/PublishStatus";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";

export function Review() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const { data: runData, isLoading: runLoading, error: runError } = useContentRun(runId);
  const { data: piecesData, isLoading: piecesLoading } = useContentPieces(runId);

  const approvePiece = useApprovePiece();
  const rejectPiece = useRejectPiece();
  const updatePiece = useUpdatePiece();
  const regeneratePiece = useRegeneratePiece(runId || "");
  const publishRun = usePublishRun(runId || "");
  const { data: publishStatusData } = usePublishStatus(runId);
  const retryPublish = useRetryPublish();

  if (runLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (runError || !runData?.data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <p className="mb-2 text-sm text-red-600">
          Failed to load content run.
        </p>
        <Button variant="secondary" size="sm" onClick={() => navigate("/dashboard")}>
          Back to dashboard
        </Button>
      </div>
    );
  }

  const run = runData.data;
  const pieces = piecesData?.data || [];
  const selectedIdea = run.selected_idea_index !== null ? run.ideas[run.selected_idea_index] : null;
  const approvedCount = pieces.filter((p) => p.status === "approved").length;

  // Pipeline status
  const isProcessing = Object.values(run.agent_status).some(
    (s) => s === "working" || s === "queued",
  );
  const hasFailed = Object.values(run.agent_status).some((s) => s === "failed");

  const handleApprove = (id: string) => {
    approvePiece.mutate(id);
  };

  const handleReject = (id: string) => {
    rejectPiece.mutate(id);
  };

  const handleEdit = (id: string, content: string) => {
    updatePiece.mutate({
      id,
      data: { content, character_count: content.length },
    });
  };

  const handleRegenerate = (id: string) => {
    const piece = pieces.find((p) => p.id === id);
    if (piece) {
      regeneratePiece.mutate({ platform: piece.platform });
    }
  };

  const handlePublishAll = () => {
    publishRun.mutate();
  };

  const handleRetryPublish = (pieceId: string) => {
    retryPublish.mutate(pieceId);
  };

  // Merge pieces with publish status data when available
  const publishedPieces = publishStatusData?.data || pieces;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="mb-2 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            Back to dashboard
          </button>
          <h1 className="text-xl font-bold text-slate-900">
            {selectedIdea?.headline || "Review Content"}
          </h1>
          {selectedIdea && (
            <p className="mt-1 text-sm text-slate-500">{selectedIdea.angle}</p>
          )}
        </div>
        <div className="shrink-0">
          {isProcessing && (
            <Badge variant="warning">Processing</Badge>
          )}
          {hasFailed && (
            <Badge variant="error">Failed</Badge>
          )}
          {!isProcessing && !hasFailed && (
            <Badge variant="success">Ready</Badge>
          )}
        </div>
      </div>

      {/* Pipeline progress (show when processing or failed) */}
      {(isProcessing || hasFailed) && (
        <PipelineStatus agentStatus={run.agent_status} />
      )}

      {/* Content review grid */}
      <ContentReview
        pieces={pieces}
        onApprove={handleApprove}
        onReject={handleReject}
        onEdit={handleEdit}
        onRegenerate={handleRegenerate}
        loading={piecesLoading}
      />

      {/* Publishing status */}
      {publishedPieces.some((p) => p.publish_status !== null) && (
        <PublishStatus
          pieces={publishedPieces}
          onRetry={handleRetryPublish}
        />
      )}

      {/* View Performance link */}
      {publishedPieces.some((p) => p.publish_status === "published") && (
        <Link
          to={`/performance/${runId}`}
          className="inline-flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
            />
          </svg>
          View Performance
        </Link>
      )}

      {/* Footer: Publish All Approved */}
      {pieces.length > 0 && (
        <div className="sticky bottom-20 flex justify-end sm:bottom-4">
          <PublishButton
            runId={runId || ""}
            approvedCount={approvedCount}
            onPublish={handlePublishAll}
            isPublishing={publishRun.isPending}
          />
        </div>
      )}
    </div>
  );
}
